import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Navigation } from '../components/Navigation';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_TASK_TEMPLATES = gql`
  query ListTaskTemplates {
    listTaskTemplates {
      id
      name
      title
      description
      taskType
      priority
      defaultDueDays
      category
      createdAt
      updatedAt
    }
  }
`;

const CREATE_TASK_TEMPLATE = gql`
  mutation CreateTaskTemplate($input: CreateTaskTemplateInput!) {
    createTaskTemplate(input: $input) {
      id
      name
      title
      description
      taskType
      priority
      defaultDueDays
      category
      createdAt
    }
  }
`;

const UPDATE_TASK_TEMPLATE = gql`
  mutation UpdateTaskTemplate($id: ID!, $input: UpdateTaskTemplateInput!) {
    updateTaskTemplate(id: $id, input: $input) {
      id
      name
      title
      description
      taskType
      priority
      defaultDueDays
      category
      updatedAt
    }
  }
`;

const DELETE_TASK_TEMPLATE = gql`
  mutation DeleteTaskTemplate($id: ID!) {
    deleteTaskTemplate(id: $id) {
      id
    }
  }
`;

interface TaskTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  taskType: string;
  priority: string;
  defaultDueDays: number;
  category: string;
  createdAt: string;
  updatedAt: string;
}

// Type definitions for Apollo queries/mutations
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface ListTaskTemplatesData {
  listTaskTemplates: TaskTemplate[];
}

export function TaskTemplates() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);

  // Form state
  const [templateName, setTemplateName] = useState('');
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateType, setTemplateType] = useState('DOCUMENT_UPLOAD');
  const [templatePriority, setTemplatePriority] = useState('MEDIUM');
  const [templateDueDays, setTemplateDueDays] = useState('7');
  const [templateCategory, setTemplateCategory] = useState('MEDICAL');

  const { data: userData, loading: userLoading } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data, loading, error, refetch } = useQuery<ListTaskTemplatesData>(LIST_TASK_TEMPLATES);
  const [createTemplate] = useMutation(CREATE_TASK_TEMPLATE);
  const [updateTemplate] = useMutation(UPDATE_TASK_TEMPLATE);
  const [deleteTemplate] = useMutation(DELETE_TASK_TEMPLATE);

  const templates: TaskTemplate[] = data?.listTaskTemplates || [];
  const user = userData?.getCurrentUser;

  const categories = ['ALL', 'MEDICAL', 'FINANCIAL', 'EVIDENCE', 'LEGAL', 'COMMUNICATION'];
  const taskTypes = [
    'DOCUMENT_UPLOAD',
    'APPOINTMENT',
    'FORM_COMPLETION',
    'COMMUNICATION',
    'REVIEW',
    'COMPLIANCE',
    'OTHER'
  ];
  const priorities = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'ALL' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Group templates by category
  const templatesByCategory = categories
    .filter(cat => cat !== 'ALL')
    .reduce((acc, category) => {
      acc[category] = filteredTemplates.filter(t => t.category === category);
      return acc;
    }, {} as Record<string, TaskTemplate[]>);

  const resetForm = () => {
    setTemplateName('');
    setTemplateTitle('');
    setTemplateDescription('');
    setTemplateType('DOCUMENT_UPLOAD');
    setTemplatePriority('MEDIUM');
    setTemplateDueDays('7');
    setTemplateCategory('MEDICAL');
  };

  const handleCreateTemplate = async () => {
    try {
      await createTemplate({
        variables: {
          input: {
            name: templateName,
            title: templateTitle,
            description: templateDescription,
            taskType: templateType,
            priority: templatePriority,
            defaultDueDays: parseInt(templateDueDays),
            category: templateCategory
          }
        }
      });
      setShowCreateModal(false);
      resetForm();
      refetch();
    } catch (err) {
      console.error('Error creating template:', err);
      alert('Failed to create template');
    }
  };

  const handleEditTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      await updateTemplate({
        variables: {
          id: selectedTemplate.id,
          input: {
            name: templateName,
            title: templateTitle,
            description: templateDescription,
            taskType: templateType,
            priority: templatePriority,
            defaultDueDays: parseInt(templateDueDays),
            category: templateCategory
          }
        }
      });
      setShowEditModal(false);
      setSelectedTemplate(null);
      resetForm();
      refetch();
    } catch (err) {
      console.error('Error updating template:', err);
      alert('Failed to update template');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      await deleteTemplate({
        variables: { id: selectedTemplate.id }
      });
      setShowDeleteModal(false);
      setSelectedTemplate(null);
      refetch();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template');
    }
  };

  const openEditModal = (template: TaskTemplate) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateTitle(template.title);
    setTemplateDescription(template.description);
    setTemplateType(template.taskType);
    setTemplatePriority(template.priority);
    setTemplateDueDays(template.defaultDueDays.toString());
    setTemplateCategory(template.category);
    setShowEditModal(true);
  };

  const openDeleteModal = (template: TaskTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const getTaskTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      DOCUMENT_UPLOAD: '📄',
      APPOINTMENT: '📅',
      FORM_COMPLETION: '📝',
      COMMUNICATION: '💬',
      REVIEW: '👁️',
      COMPLIANCE: '✅',
      OTHER: '📋'
    };
    return icons[type] || '📋';
  };

  const getPriorityBadgeVariant = (priority: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      LOW: 'secondary',
      MEDIUM: 'default',
      HIGH: 'destructive',
      URGENT: 'destructive'
    };
    return variants[priority] || 'default';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      MEDICAL: 'bg-blue-100 text-blue-800 border-blue-200',
      FINANCIAL: 'bg-green-100 text-green-800 border-green-200',
      EVIDENCE: 'bg-purple-100 text-purple-800 border-purple-200',
      LEGAL: 'bg-red-100 text-red-800 border-red-200',
      COMMUNICATION: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (userLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold text-red-600">Error loading templates</h2>
            <p className="text-gray-600 mt-2">{error.message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Task Templates</h1>
            </div>
            <Button
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              className="hover:bg-blue-700"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              Create Template
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="mb-6 flex gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search templates by name, title, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md bg-white"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{templates.length}</p>
                <p className="text-sm text-gray-600 mt-1">Total Templates</p>
              </div>
            </CardContent>
          </Card>
          {categories.filter(c => c !== 'ALL').map(category => (
            <Card key={category}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">
                    {templates.filter(t => t.category === category).length}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{category}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Templates by Category */}
        {selectedCategory === 'ALL' ? (
          // Show all categories
          Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
            categoryTemplates.length > 0 && (
              <div key={category} className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm ${getCategoryColor(category)}`}>
                    {category}
                  </span>
                  <span className="text-gray-500 text-sm">({categoryTemplates.length})</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryTemplates.map(template => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onEdit={openEditModal}
                      onDelete={openDeleteModal}
                      getTaskTypeIcon={getTaskTypeIcon}
                      getPriorityBadgeVariant={getPriorityBadgeVariant}
                      getCategoryColor={getCategoryColor}
                    />
                  ))}
                </div>
              </div>
            )
          ))
        ) : (
          // Show selected category only
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
                getTaskTypeIcon={getTaskTypeIcon}
                getPriorityBadgeVariant={getPriorityBadgeVariant}
                getCategoryColor={getCategoryColor}
              />
            ))}
          </div>
        )}

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <span className="text-6xl mb-4 block">📋</span>
            <p className="text-gray-600 text-lg mb-2">No templates found</p>
            <p className="text-gray-500 text-sm mb-4">
              {searchQuery ? 'Try adjusting your search' : 'Create your first template to get started'}
            </p>
            <Button
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              className="hover:bg-blue-700"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              Create Template
            </Button>
          </div>
        )}
      </main>

      {/* Create/Edit Template Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {showCreateModal ? 'Create Task Template' : 'Edit Task Template'}
            </h3>
            
            <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name <span className="text-red-600">*</span>
                </label>
                <Input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Initial Medical Records Upload"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Title <span className="text-red-600">*</span>
                </label>
                <Input
                  type="text"
                  value={templateTitle}
                  onChange={(e) => setTemplateTitle(e.target.value)}
                  placeholder="e.g., Upload Initial Medical Records"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Provide detailed instructions for the client..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {categories.filter(c => c !== 'ALL').map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Type <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {taskTypes.map(type => (
                      <option key={type} value={type}>{type.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={templatePriority}
                    onChange={(e) => setTemplatePriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Due Days <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    value={templateDueDays}
                    onChange={(e) => setTemplateDueDays(e.target.value)}
                    min="1"
                    placeholder="7"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  showCreateModal ? setShowCreateModal(false) : setShowEditModal(false);
                  setSelectedTemplate(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
                className="flex-1 hover:bg-blue-700"
                onClick={showCreateModal ? handleCreateTemplate : handleEditTemplate}
                disabled={!templateName || !templateTitle || !templateDescription}
              >
                {showCreateModal ? 'Create Template' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Template</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the template "{selectedTemplate.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTemplate(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteTemplate}
              >
                Delete Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Template Card Component
interface TemplateCardProps {
  template: TaskTemplate;
  onEdit: (template: TaskTemplate) => void;
  onDelete: (template: TaskTemplate) => void;
  getTaskTypeIcon: (type: string) => string;
  getPriorityBadgeVariant: (priority: string) => 'default' | 'secondary' | 'destructive' | 'outline';
  getCategoryColor: (category: string) => string;
}

function TemplateCard({ 
  template, 
  onEdit, 
  onDelete, 
  getTaskTypeIcon, 
  getPriorityBadgeVariant,
  getCategoryColor 
}: TemplateCardProps) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{getTaskTypeIcon(template.taskType)}</span>
              <Badge variant={getPriorityBadgeVariant(template.priority)}>
                {template.priority}
              </Badge>
            </div>
            <CardTitle className="text-lg">{template.name}</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-700">{template.title}</p>
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{template.description}</p>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className={`px-2 py-1 rounded-full ${getCategoryColor(template.category)}`}>
              {template.category}
            </span>
            <span>•</span>
            <span>{template.taskType.replace('_', ' ')}</span>
            <span>•</span>
            <span>Due in {template.defaultDueDays} days</span>
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <Button
              variant="outline"
              className="flex-1 text-sm"
              onClick={() => onEdit(template)}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              className="flex-1 text-sm border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => onDelete(template)}
            >
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
